import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import type {AnalyticsEvent} from "@khanacademy/event-schemas";
import BirthdayPicker from "@khanacademy/wonder-blocks-birthday-picker";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";

type BirthdayPickerProps = JSX.LibraryManagedAttributes<
    typeof BirthdayPicker,
    React.ComponentProps<typeof BirthdayPicker>
>;

type OwnProps = {
    // Text to display as the label for the BirthdayPicker.
    label?: string;
    // Whether entering a birthday is required. Will display a red asterisk.
    // NOTE: This does not actually enforce the requirement, it's just a visual
    // indicator.
    required?: boolean;
    // CEDAR event to fire when the BirthdayPicker is changed. Note that this
    // only fires when the date is valid. So if the user selects month first,
    // then day, the event doesn't fire until the year is selected. But if the
    // component has a default value, the event will fire immediately on change
    // of any dropdown.
    cedarEvent?: AnalyticsEvent;
};

type CombinedProps = OwnProps & BirthdayPickerProps;
type Props = Omit<CombinedProps, "monthYearOnly">;

export default function InnerBirthdayPicker(props: Props): React.ReactElement {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    /**
     * Handle a change to the WB BirthdayPicker component, confirming if the
     * input is valid, and then reporting the result back to the calling
     * component via onChange.
     */
    const handleChange = (dateValue?: string | null) => {
        // Publish the CEDAR event if it exists and the date is valid.
        if (props.cedarEvent && dateValue !== null) {
            publishAnalyticsEvent(props.cedarEvent);
        }

        // NOTE(juan): When `monthYearOnly` is set `true`, this dateValue is
        // passing the LAST day of the month. Make sure to modify it here
        // before passing the value to the callers.
        props.onChange(dateValue);
    };

    const {
        defaultValue,
        disabled,
        labels,
        label,
        required,
        style,
        dropdownStyle,
    } = props;

    return (
        <View>
            <BirthdayPickerLabel label={label} required={required}>
                <BirthdayPicker
                    defaultValue={defaultValue}
                    disabled={disabled}
                    monthYearOnly={true}
                    onChange={handleChange}
                    labels={
                        labels || {
                            month: t`Month`,
                            day: t`Day`,
                            year: t`Year`,
                            errorMessage: t`Please select a valid birthdate.`,
                        }
                    }
                    style={style}
                    dropdownStyle={dropdownStyle}
                />
            </BirthdayPickerLabel>
        </View>
    );
}

function BirthdayPickerLabel({
    label,
    required,
    children,
}: {
    label?: string;
    required?: boolean;
    children: React.ReactNode;
}): React.ReactNode {
    return (
        <>
            {/* Because we don't have access to the BirthdayPicker's id
        attribute, we'll instead wrap the component in the Label for the
        same accessibility effect. */}
            {label ? (
                <fieldset className={css(styles.fieldset)}>
                    <LabelMedium tag="legend">
                        {label}{" "}
                        {required && (
                            <span
                                aria-label={t`required`}
                                className={css(styles.requiredIndicator)}
                            >
                                *
                            </span>
                        )}
                    </LabelMedium>
                    <Strut size={spacing.small_12} />
                    {children}
                </fieldset>
            ) : (
                children
            )}
        </>
    );
}

const styles = StyleSheet.create({
    fieldset: {
        display: "flex",
        flexDirection: "column",
    },
    requiredIndicator: {
        color: color.red,
    },
});
